import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { translations } from '../utils/translations';

const Contact = ({ language = 'en' }) => {
  const { toast } = useToast();
  const t = translations[language];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    market: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const markets = [
    t.contact.marketIbiza || 'Ibiza',
    t.contact.marketOther || 'Other'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // PHP contact form handler
      const response = await fetch('/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          market: formData.market,
          message: formData.message
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: t.contact.toastTitle,
          description: t.contact.toastDescription,
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          market: '',
          message: ''
        });
      } else {
        throw new Error(result.message || 'Form submission failed');
      }
    } catch (error) {
      toast({
        title: language === 'en' ? 'Error' : language === 'de' ? 'Fehler' : language === 'it' ? 'Errore' : 'Error',
        description: language === 'en' ? 'Something went wrong. Please try again or email us directly at Office@atelierprimeibiza.com' : 
                     language === 'de' ? 'Etwas ist schief gelaufen. Bitte versuchen Sie es erneut oder senden Sie uns eine E-Mail an Office@atelierprimeibiza.com' :
                     language === 'it' ? 'Qualcosa è andato storto. Per favore riprova o inviaci un\'email a Office@atelierprimeibiza.com' :
                     'Algo salió mal. Por favor, inténtalo de nuevo o envíanos un correo a Office@atelierprimeibiza.com',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-image">
          <img 
            src="https://images.unsplash.com/photo-1543489822-c49534f3271f" 
            alt="Luxury Pool Design"
          />
          <div className="contact-hero-overlay"></div>
        </div>
        <div className="contact-hero-content">
          <p className="page-overline">{t.contact.overline}</p>
          <h1>{t.contact.title}</h1>
          <p className="contact-hero-description">
            {t.contact.description}
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="section-container">
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info">
              <div className="contact-info-header">
                <h2>{t.contact.companyName}</h2>
                <p>{t.contact.companyTagline}</p>
              </div>

              <div className="contact-details">
                <div className="contact-detail-item">
                  <MapPin size={20} strokeWidth={1.5} />
                  <div>
                    <h4>{t.contact.addressLabel}</h4>
                    <p>{t.contact.address1}</p>
                    <p>{t.contact.address2}</p>
                    <p>{t.contact.address3}</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <Mail size={20} strokeWidth={1.5} />
                  <div>
                    <h4>{t.contact.emailLabel}</h4>
                    <p>Office@atelierprimeibiza.com</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <Phone size={20} strokeWidth={1.5} />
                  <div>
                    <h4>{t.contact.phoneLabel}</h4>
                    <p>+34 659 170 057</p>
                  </div>
                </div>
              </div>

              <div className="contact-note">
                <p>
                  {t.contact.note}
                </p>
              </div>
            </div>

            {/* Inquiry Form */}
            <div className="inquiry-form-container">
              <form onSubmit={handleSubmit} className="inquiry-form">
                <div className="form-group">
                  <label htmlFor="name">{t.contact.formName}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">{t.contact.formEmail}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">{t.contact.formPhone}</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="market">{t.contact.formMarket}</label>
                  <select
                    id="market"
                    name="market"
                    value={formData.market}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">{t.contact.formMarketPlaceholder}</option>
                    {markets.map((market) => (
                      <option key={market} value={market}>
                        {market}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">{t.contact.formMessage}</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                    required
                    disabled={isSubmitting}
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  <span>{isSubmitting ? t.contact.submitting : t.contact.submitButton}</span>
                  <Send size={18} strokeWidth={1.5} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Information */}
      <section className="legal-info-section">
        <div className="section-container narrow">
          <p className="legal-text">
            Atelier Prime Estates SL · CIF B26974915 · Registered in Spain
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
